<script setup lang="ts">
import { useRegister } from '@/composables/dto/useRegister';
import { RuleEnum } from '@/constants/enums/RuleEnum';
import type { SignupData } from '@/models/interface/register';
import WrongMessage from './errorAlert/WrongMessage.vue';
import WrongSex from './errorAlert/WrongSex.vue';
import WrongSignupPhone from './errorAlert/WrongSignupPhone.vue';
import FormCard from './formElement/FormCard.vue';
import FormInput from './formElement/FormInput.vue';
import AppleThirdPart from './thirdpart/AppleThirdPart.vue';
import GoogleThirdPart from './thirdpart/GoogleThirdPart.vue';
import LineThirdParty from './thirdpart/LineThirdParty.vue';
import MetaThirdParty from './thirdpart/MetaThirdParty.vue';

interface content {
  thirdPartyId: string;
  email: string;
  responseText: string;
}

const formChoose = defineModel('formChoose');
const showPopup = defineModel('showPopup');
const showAlert = defineModel('showAlert');
const information = defineModel<SignupData>('information', { required: true });

const pop = usePopStore();
const inputRefPhone = ref<InstanceType<typeof FormInput>>();
const inputRefName = ref<InstanceType<typeof FormInput>>();
const { fetchData } = useRegister();
const { googleVerifyHandler, metaVerifyHandler } = useThirdPartVerify();

const sendVerifyCode = async () => {
  information.value.verificationCode = '';

  pop.loadingStatus = true;
  const res = await fetchData({
    phone: information.value.phone,
    fullName: information.value.fullName
  });
  pop.loadingStatus = false;

  if (res) {
    if (res.isSuccess) {
      formChoose.value = 'SMSVerification';
      return;
    }

    if (res.errorCode === -4020 || res.errorCode === -4038) {
      showPopup.value = true;
      showAlert.value = WrongSignupPhone;
      return;
    }

    showPopup.value = true;
    showAlert.value = WrongMessage;
  }
};

const signup = async () => {
  const checkedPhone = inputRefPhone.value?.handleBlur() ?? false;
  const checkedName = inputRefName.value?.handleBlur() ?? false;
  if (!checkedPhone || !checkedName) return;

  if (information.value.sex === '-1') {
    showPopup.value = true;
    showAlert.value = WrongSex;
    return;
  }

  sendVerifyCode();
};

const handleGoogleVerify = (token: string) => {
  googleVerifyHandler(token);
};

const handleMetaVerify = (token: string) => {
  metaVerifyHandler(token);
};

const openServiceContract = () => {
  window.open('https://funday.asia/policy/policy.html', '_blank');
};

const isBrowser = () => {
  const userAgent = navigator.userAgent;

  // 判断用户代理字符串中是否包含特定的浏览器标识
  if (userAgent.indexOf('Chrome') !== -1) {
    return true;
  } else if (userAgent.indexOf('Safari') !== -1) {
    return true;
  } else if (userAgent.indexOf('Firefox') !== -1) {
    return true;
  } else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) {
    return true;
  } else if (userAgent.indexOf('Edge') !== -1) {
    return true;
  } else if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) {
    return true;
  } else {
    return false; // 如果无法确定浏览器类型，则返回 "Unknown"
  }
};
</script>

<template>
  <FormCard>
    <div class="title-container">
      <p>免費加入</p>
      <div class="cross-btn" @click="formChoose = ''">
        <SvgIcon color="grey" name="cross" />
      </div>
    </div>

    <div class="email-block">
      <FormInput
        ref="inputRefPhone"
        inputPlaceHolder="請輸入手機號碼"
        inputTitle="帳號"
        inputType="text"
        :verifyRule="RuleEnum.Phone"
        :required="true"
        v-model:inputValue="information.phone"
      />
    </div>

    <div class="name-and-sex-block">
      <FormInput
        ref="inputRefName"
        inputPlaceHolder="請輸入中文姓名"
        inputTitle="中文姓名"
        inputType="text"
        :verifyRule="RuleEnum.Name"
        :required="true"
        v-model:inputValue="information.fullName"
        style="margin: 0"
      ></FormInput>

      <div class="sex-block">
        <p class="sex">性別<span>*</span></p>
        <div class="choose-sex-block">
          <label class="female-female">
            <input type="radio" value="1" v-model="information.sex" />
            <div class="virtual-input"></div>
            <p>男</p>
          </label>
          <label class="female-female">
            <input type="radio" value="0" v-model="information.sex" />
            <div class="virtual-input"></div>
            <p>女</p>
          </label>
        </div>
      </div>
    </div>

    <div class="third-part-block">
      <div class="virtual-line"></div>
      <p>其他方式加入</p>
      <div class="virtual-line"></div>
    </div>

    <div class="third-part-icon-block" v-if="isBrowser()">
      <GoogleThirdPart @googleVerify="handleGoogleVerify" />
      <MetaThirdParty @metaVerify="handleMetaVerify" />
      <LineThirdParty />
      <AppleThirdPart />
    </div>

    <div class="login-btn" @click="signup">送出</div>

    <p class="hint-rule-notice" @click="openServiceContract">
      加入即代表同意FUNDAY的<span>服務條款與隱私權</span>
    </p>
  </FormCard>
</template>

<style scoped lang="scss">
.title-container {
  display: flex;
  width: 100%;
  align-items: center;
  position: relative;
  margin-bottom: 16px;
  p {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000cc;
    font-size: 18px;
    font-weight: 600;

    @include media-breakpoint-down(lg) {
      justify-content: center;
    }
  }
  .cross-btn {
    cursor: pointer;
  }
}
.hint-block {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  gap: 8px;
  margin: 4px 0 16px 0;
  .hint-que {
    color: #00000066;
  }
  .hint-join {
    color: #f74768;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
      text-decoration-color: #f74768;
    }
  }
}
.email-block {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  .send-verify-code {
    border-radius: 4px;
    margin-left: 8px;
    width: 102px;
    flex-shrink: 0;
    white-space: nowrap;
    height: 48px;
    color: $txt-color-grey-80;
    font-size: 14px;
    background-color: #f4f4f4;
    border: none;
    cursor: pointer;
  }
}
.verify-hint {
  color: $txt-color-grey-24;
  font-size: 12px;
  width: 100%;
}
.third-part-block {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
  .virtual-line {
    height: 1px;
    flex: 1;
    border: 1px solid #0000001a;
  }
  p {
    color: #00000066;
    font-size: 12px;
  }
}
.name-and-sex-block {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  .sex-block {
    display: flex;
    flex-direction: column;
    white-space: nowrap;
    .sex {
      min-width: 104px;
      color: $txt-color-grey-60;
      font-weight: 600;
      span {
        color: #f74768;
      }
    }
    .choose-sex-block {
      display: flex;
      justify-content: space-between;

      .female-female {
        display: flex;
        height: 48px;
        align-items: center;
        color: $txt-color-grey-60;
        cursor: pointer;
        input {
          display: none;
        }
        .virtual-input {
          width: 24px;
          height: 24px;
          background-position: center center;
          background-repeat: no-repeat;
          margin-right: 4px;
          background-image: url('../../assets/icon/inputRadio.svg');
          &:hover {
            background-image: url('../../assets/icon/inputRadioHover.svg');
          }
        }
        input:checked ~ .virtual-input {
          background-image: url('../../assets/icon/inputRadioChecked.svg');
          &:hover {
            background-image: url('../../assets/icon/inputRadioCheckedAndHover.svg');
          }
        }
      }
    }
  }
}

.junior-landing-page > .main-content > .card > .login-btn {
  background-color: #d44934;
}

.junior-landing-page > .main-content > .card > .login-btn:hover {
  background-color: #7f2c1f;
}

.login-btn {
  margin: 16px 0;
  background-color: #f74768;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #ffffff;
  height: 40px;
  width: 100%;
  cursor: pointer;
}
.third-part-icon-block {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}
.hint-rule-notice {
  width: 100%;
  display: flex;
  justify-content: start;
  align-items: center;
  font-size: 12px;
  color: $txt-color-grey-40;
  font-size: 12px;

  @include media-breakpoint-down(lg) {
    justify-content: center;
  }

  span {
    position: relative;
    cursor: pointer;
    &:after {
      content: '';
      width: 100%;
      bottom: -2px;
      left: 0;
      height: 1px;
      background-color: $txt-color-grey-40;
      position: absolute;
    }
  }
}
</style>
